import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Autocomplete,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import DynamicTable from "../../../../components/lab-dynamic-table";
import DeleteConfirmationModal from "../../../../components/Modals/delete-modal";
import { Add, AddIcCallOutlined, Cancel, FileDownload, Print } from "@mui/icons-material";
import * as xlsx from "xlsx";
import jsPDF from "jspdf";
import { formatCurrency } from "../../../../utils/numberUtils";
import autoTable from "jspdf-autotable";

interface DoctorShare {
  doctorId: string;
  share: string;
  shareMode: string;
}

interface Department {
  _id: string;
  department: string;
}
const gradientButtonStyle = {
  minWidth: { xs: "auto", md: "80px" },
  fontSize: { xs: "12px", md: "12px" },
  fontWeight: "600",
  textTransform: "none",
  borderRadius: "8px",
  background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
  color: "#ffffff",
  boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
  transition: "all 0.3s ease-in-out",
  "&:hover": {
    background: "linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)",
    boxShadow: "0 5px 15px rgba(25, 118, 210, .4)",
    transform: "translateY(-1px)",
  },
};
const IncomeCategoryTab: React.FC = () => {
  const [editData, setEditData] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState("");
  const [loading, setLoading] = useState(false);
  const [incomeData, setIncomeData] = useState<any[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [departments, setDepartments] = useState<Department[]>([]);
  const [doctors, setDoctors] = useState<any[]>([]);

  const [formData, setFormData] = useState<{
    categoryName: string;
    description: string;
    price: string;
    departmentId: string;
    discount: string;
    discountMode: string;
    doctorShare: DoctorShare[];
  }>({
    categoryName: "",
    description: "",
    price: "",
    departmentId: "",
    discount: "",
    discountMode: "%",
    doctorShare: [],
  });

  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 500);
    return () => {
      clearTimeout(handler);
    };
  }, [searchInput]);

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const updateDoctorShare = (index: number, field: string, value: string) => {
    setFormData(prev => {
      const newShares = [...prev.doctorShare];
      newShares[index][field as keyof DoctorShare] = value;
      return { ...prev, doctorShare: newShares };
    });
  };

  const addDoctorShare = () => {
    setFormData(prev => ({
      ...prev,
      doctorShare: [
        ...prev.doctorShare,
        { doctorId: "", share: "", shareMode: "%" },
      ],
    }));
  };

  const removeDoctorShare = (index: number) => {
    setFormData(prev => ({
      ...prev,
      doctorShare: prev?.doctorShare?.filter((_, i) => i !== index),
    }));
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}/acc-department/getAllByPagination`,
        {
          params: { pageNo: 1, count: 1000 },
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      // Use '?? []' to ensure the state is always an array
      setDepartments(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching departments:", error);
      // Also ensure it's an array on error
      setDepartments([]);
    }
  };

  const fetchDoctors = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}/users/getAllSharerDoctors`,
        {
          params: { pageNo: 1, count: 500 },
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setDoctors(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching doctors:", error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}/income-category/getAllProcedureByPagination`,
        {
          params: {
            pageNo: page + 1,
            count: rowsPerPage,
            search: debouncedSearch,
          },
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setIncomeData(response.data?.data);
      setTotal(response.data?.totalCount);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error fetching data");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditClick = (id: string, rowData: any) => {
    setEditData(id);
    setFormData({
      categoryName: rowData?.categoryName || "",
      description: rowData?.description || "",
      price: rowData?.price || "",
      departmentId: rowData?.departmentId || "",
      discount: rowData?.discount || "",
      discountMode: rowData?.discountMode || "%",
      doctorShare: rowData?.doctorShare || [],
    });
  };

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, debouncedSearch]);

  useEffect(() => {
    fetchDepartments();
    fetchDoctors();
  }, []);

  const handleSubmit = async () => {
    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price) || 0,
        discount: parseFloat(formData.discount) || 0,
        doctorShare: formData.doctorShare.map(share => ({
          ...share,
          share: parseFloat(share.share) || 0,
        })),
      };
      if (editData) {
        const response = await axios.patch(
          `${process.env.REACT_APP_ADMIN_API_URL}/income-category/updateIncomeProcedureById/${editData}`,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (!response?.data?.isSuccess) {
          toast.error(response?.data?.message);
        } else {
          toast.success(response?.data?.message);
          resetForm();
          fetchData();
        }
      } else {
        const response = await axios.post(
          `${process.env.REACT_APP_ADMIN_API_URL}/income-category/createIncomeProcedure`,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (!response?.data?.isSuccess) {
          toast.error(response?.data?.message);
        } else {
          toast.success(response?.data?.message);
          resetForm();
          fetchData();
        }
      }
    } catch (error: any) {
      toast.error(error.message || "Something went wrong");
    }
  };

  const resetForm = () => {
    setFormData({
      categoryName: "",
      description: "",
      price: "",
      departmentId: "",
      discount: "",
      discountMode: "%",
      doctorShare: [],
    });
    setEditData(null);
  };

  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
  };

  const handleDeleteClick = (id: string) => {
    setIsDeleteModalOpen(true);
    setSelectedItemId(id);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_ADMIN_API_URL}/income-category/deleteById/${selectedItemId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      toast.success(response.data.message);
      resetForm();
      fetchData();
    } catch (error: any) {
      toast.error(error.message || "Something went wrong");
    }
    setIsDeleteModalOpen(false);
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };


  const handleExcelExport = () => {
  if (!incomeData || incomeData.length === 0) {
    toast.warn("No data to export.");
    return;
  }

  // Map the procedure data exactly like your table
  const worksheetData = incomeData.map((item: any) => ({
    "SN": "", // Will be filled in order later if needed
    "Procedure Name": item.categoryName || "--",
    "Description": item.description || "--",
    "Price": Number(item.price) || 0,
    "Department": departments.find((d: any) => d._id === item.departmentId)?.department || "--",
    "Discount": item.discount
      ? `${item.discount} ${item.discountMode === "value" ? "Rs" : "%"}`
      : "0",
  }));

  // Calculate totals
  const totalPrice = incomeData.reduce(
    (sum: number, item: any) => sum + (Number(item.price) || 0),
    0
  );

  // Add TOTAL row (exactly like your original style)
  worksheetData.push({
    "SN": "",
    "Procedure Name": "TOTAL",
    "Description": "",
    "Price": totalPrice,
    "Department": "",
    "Discount": "",
  } as any);

  // Optional: Add serial numbers (1, 2, 3...) in the first column
  worksheetData.forEach((row, index) => {
    if (index < worksheetData.length - 1) {
      (row as any)["SN"] = index + 1;
    }
  });

  // Create and download Excel file
  const worksheet = xlsx.utils.json_to_sheet(worksheetData);
  const workbook = xlsx.utils.book_new();
  xlsx.utils.book_append_sheet(workbook, worksheet, "IncomeProcedures");

  // Use current date or date range if you have fromDate/toDate
  const today = new Date().toISOString().split("T")[0];
  xlsx.writeFile(workbook, `Income_Procedures_${today}.xlsx`);

  toast.success("Excel exported successfully!");
};



const handlePrint = () => {
  if (!incomeData || incomeData.length === 0) {
    toast.warn("No data to print.");
    return;
  }

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 8;
  let yPosition = margin;

  // Professional Header
  doc
    .setFontSize(10)
    .setFont("helvetica", "bold")
    .setTextColor(25, 55, 85)
    .text("INCOME PROCEDURE REPORT", pageWidth / 2, yPosition, {
      align: "center",
    });

  yPosition += 6;
  doc
    .setDrawColor(25, 55, 85)
    .setLineWidth(0.5)
    .line(margin, yPosition, pageWidth - margin, yPosition);

  // Metadata (Generated date)
  yPosition += 3;
  doc
    .setFontSize(6.5)
    .setFont("helvetica", "normal")
    .setTextColor(80, 80, 80)
    .text(
      `Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`,
      pageWidth / 2,
      yPosition,
      { align: "center" }
    );

  yPosition += 6;

  // Table Headers
  const head = [
    [
      "SN",
      "PROCEDURE NAME",
      "DESCRIPTION",
      "PRICE",
      "DEPARTMENT",
      "DISCOUNT",
    ],
  ];

  // Table Body
  const body = incomeData.map((item: any, index: number) => [
    index + 1,
    item.categoryName || "--",
    item.description || "--",
    formatCurrency(item.price || 0),
    departments.find((d: any) => d._id === item.departmentId)?.department || "--",
    item.discount
      ? `${item.discount} ${item.discountMode === "value" ? "Rs" : "%"}`
      : "0",
  ]);

  // Calculate Total Price
  const totalPrice = incomeData.reduce(
    (sum: number, item: any) => sum + Number(item.price || 0),
    0
  );

  // Add TOTAL Row
  body.push([
    "",
    "TOTAL",
    "",
    formatCurrency(totalPrice),
    "",
    "",
  ]);

  // Professional Table Styling (Same as Doctor Share Summary)
 autoTable(doc, {
  startY: yPosition,
  head,
  body,

  columnStyles: {
    0: { cellWidth: 10, halign: "center" },  // SN
    1: { cellWidth: 40 ,halign: "center"},                    // PROCEDURE NAME
    2: { cellWidth: 50 ,halign: "center"},                    // DESCRIPTION
    3: { cellWidth: 22, halign: "center" },   // PRICE
    4: { cellWidth: 28,halign:"center" },                    // DEPARTMENT
    5: { cellWidth: 20, halign: "center" },  // DISCOUNT
  },

  headStyles: {
    fillColor: [25, 55, 85],
    textColor: [255, 255, 255],
    fontStyle: "bold",
    fontSize: 6,  
    halign: "center",
    valign: "middle",
    cellPadding: 2,
  },

  bodyStyles: {
    fontSize: 5.5,
    textColor: [40, 40, 40],
    cellPadding: 1.8,
  },

  alternateRowStyles: { fillColor: [248, 249, 250] },

  margin: { top: margin, right: margin, bottom: margin, left: margin+10 },

  didParseCell(data) {
    if (data.row.index === body.length - 1) {
      data.cell.styles.fillColor = [25, 55, 85];
      data.cell.styles.textColor = [255, 255, 255];
      data.cell.styles.fontStyle = "bold";
      data.cell.styles.fontSize = 5.8;
      data.cell.styles.lineColor = [200, 200, 200];
      data.cell.styles.lineWidth = 0.3;
      data.cell.styles.cellPadding = { top: 2.5, right: 2, bottom: 2.5, left: 2 };
    }
  },

  didDrawPage(data) {
    const pageSize = doc.internal.pageSize;
    const pageCount = doc.getNumberOfPages();
    doc.setFontSize(5);
    doc.setTextColor(120, 120, 120);
    doc.text(
      `Page ${data.pageNumber} of ${pageCount}`,
      pageSize.getWidth() / 2,
      pageSize.getHeight() - 4,
      { align: "center" }
    );
  },
});


  // Auto print + open preview
  doc.autoPrint();
  const blob = doc.output("blob");
  const blobUrl = URL.createObjectURL(blob);
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "none";
  document.body.appendChild(iframe);
  
  const cleanup = () => {
    if (document.body.contains(iframe)) {
      document.body.removeChild(iframe);
      URL.revokeObjectURL(blobUrl);
    }
    window.removeEventListener("focus", cleanup);
  };
  
  window.addEventListener("focus", cleanup);
  iframe.src = blobUrl;
  
  iframe.onload = () => {
    try {
      const win = iframe.contentWindow;
      if (win) {
        win.focus();
        win.print();
      }
    } catch (e) {
      console.error("Error triggering print dialog:", e);
      cleanup();
    }
  };
  
  // Fallback print trigger
  setTimeout(() => {
    try {
      const win = iframe.contentWindow;
      if (win && document.body.contains(iframe)) {
        win.focus();
        win.print();
      }
    } catch (e) {
      console.error("Fallback print attempt failed:", e);
      cleanup();
    }
  }, 1000);
};

  return (
    <Box>
      <Box
        sx={{
          borderRadius: 2,
          padding: "1px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
          }}
        >
          Procedure
        </Typography>
        
        <Box display="flex" alignItems="center" gap={1}>
            <Box
                sx={{
                  p: 1,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<FileDownload />}
                    onClick={handleExcelExport}
                    sx={gradientButtonStyle}
                  >
                    Excel
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Print />}
                    onClick={handlePrint}
                    sx={gradientButtonStyle}
                  >
                    Print
                  </Button>
                </Box>
              </Box>
          <TextField
            label="Search"
            placeholder="Search procedure..."
            variant="outlined"
            value={searchInput}
            className="custom-input"
            onChange={handleSearchInputChange}
            size="small"
            InputProps={{
              endAdornment: (
                <IconButton>
                  <SearchIcon sx={{ color: "#055FFC" }} />
                </IconButton>
              ),
            }}
          />
        </Box>
      </Box>

      <Grid container spacing={1}>
        <Grid item xs={12} sm={5} md={5}>
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: 2,
              p: 2,
              boxShadow: 1,
            }}
          >
            <Grid container spacing={2} sx={{ mb: 1 }}>
              <Grid item xs={6}>
                <TextField
                  size="small"
                  className="custom-input"
                  label="Procedure Name"
                  name="categoryName"
                  value={formData.categoryName}
                  fullWidth
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  size="small"
                  className="custom-input"
                  label="Description"
                  name="description"
                  value={formData.description}
                  fullWidth
                  onChange={handleChange}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ mb: 1 }}>
              <Grid item xs={6}>
                <TextField
                  className="custom-input"
                  size="small"
                  label="Price"
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <Autocomplete
                  size="small"
                  options={departments}
                  getOptionLabel={option => option.department}
                  value={
                    departments?.find(
                      dep => dep._id === formData.departmentId
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    setFormData(prev => ({
                      ...prev,
                      departmentId: newValue?._id || "",
                    }));
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Department"
                      className="custom-input"
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ mb: 1 }}>
              <Grid item xs={6}>
                <TextField
                  className="custom-input"
                  size="small"
                  label="Discount"
                  type="number"
                  name="discount"
                  value={formData.discount}
                  inputProps={
                    formData.discountMode === "%"
                      ? { max: 100, min: 0 }
                      : { min: 0 }
                  }
                  onChange={e => {
                    const value = Number(e.target.value);
                    if (formData.discountMode === "%" && value > 100) return;
                    handleChange(e);
                  }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ backgroundColor: "white" }}>
                    Discount Mode
                  </InputLabel>
                  <Select
                    size="small"
                    className="custom-input"
                    value={formData.discountMode}
                    name="discountMode"
                    onChange={handleChange}
                  >
                    <MenuItem value="%">%</MenuItem>
                    <MenuItem value="value">Value</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Typography
              variant="body1"
              sx={{
                mb: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              Select Doctor Share
              <Tooltip title="Add Doctor Share">
                <Add
                  sx={{
                    ml: 1,
                    cursor: "pointer",
                    bgcolor: "green",
                    color: "white",
                    borderRadius: "100%",
                  }}
                  onClick={addDoctorShare}
                />
              </Tooltip>
            </Typography>
            <div className="max-h-[240px] overflow-y-auto overflow-x-hidden py-3">
              {formData.doctorShare.map((share: DoctorShare, index: number) => (
                <Grid
                  container
                  spacing={1}
                  key={index}
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Grid item xs={4}>
                    <Autocomplete
                      size="small"
                      options={doctors}
                      getOptionLabel={option => option.fullName}
                      value={
                        doctors?.find(doc => doc._id === share.doctorId) || null
                      }
                      onChange={(event, newValue) => {
                        updateDoctorShare(
                          index,
                          "doctorId",
                          newValue?._id || ""
                        );
                      }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label="Select Doctor"
                          className="custom-input"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      className="custom-input"
                      size="small"
                      label="Share"
                      type="number"
                      value={share.share}
                      onChange={e => {
                        const value = Number(e.target.value);
                        if (share.shareMode === "%" && value > 100) return;
                        updateDoctorShare(index, "share", e.target.value);
                      }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={2.7}>
                    <FormControl fullWidth size="small">
                      <InputLabel sx={{ backgroundColor: "white" }}>
                        Share Mode
                      </InputLabel>
                      <Select
                        className="custom-input"
                        value={share.shareMode}
                        onChange={e =>
                          updateDoctorShare(index, "shareMode", e.target.value)
                        }
                      >
                        <MenuItem value="%">%</MenuItem>
                        <MenuItem value="value">Value</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={0.5}>
                    <IconButton onClick={() => removeDoctorShare(index)}>
                      <Cancel
                        sx={{ fontSize: "16px", color: "red", padding: "0px" }}
                      />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
            </div>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleSubmit}
              disabled={
                !formData.categoryName ||
                !formData.price ||
                !formData.departmentId
              }
            >
              {editData ? "Update" : "Save"}
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12} sm={7} md={7}>
          <DynamicTable
            headers={[
              "SN",
              "procedure Name",
              "Description",
              "Price",
              "Department",
              "Discount",
              "Actions",
            ]}
            data={incomeData?.map((item: any, index: number) => [
              page * rowsPerPage + index + 1,
              item?.categoryName || "--",
              item?.description || "--",
              item?.price || 0,
              departments?.find((d: any) => d._id === item.departmentId)
                ?.department || "--",
              item.discount
                ? `${item.discount} ${
                    item.discountMode === "value"
                      ? "Rs"
                      : item.discountMode || ""
                  }`
                : 0,
            ])}
            loading={loading}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRows={total}
            actionButtons={[
              {
                icon: <EditIcon sx={{ color: "#FFB800", cursor: "pointer" }} />,
                onClick: (rowIndex: number) =>
                  handleEditClick(
                    incomeData[rowIndex]?._id,
                    incomeData[rowIndex]
                  ),
              },
              {
                icon: (
                  <DeleteIcon sx={{ color: "#FF5454", cursor: "pointer" }} />
                ),
                onClick: (rowIndex: number) =>
                  handleDeleteClick(incomeData[rowIndex]?._id),
              },
            ]}
            height={"60vh"}
          />
          {incomeData?.length === 0 && (
            <h1
              style={{
                color: "gray",
                marginTop: "8px",
                textAlign: "center",
                fontSize: "12px",
                fontWeight: "bold",
                marginBottom: "8px",
              }}
            >
              No data found
            </h1>
          )}
        </Grid>
      </Grid>

      <DeleteConfirmationModal
        open={isDeleteModalOpen}
        handleClose={handleDeleteModalClose}
        handleConfirm={handleDeleteConfirm}
      />
    </Box>
  );
};

export default IncomeCategoryTab;
